import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import {
  FaTicketAlt,
  FaSearch,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaUsers,
  FaCreditCard,
  FaTag,
} from "react-icons/fa";
import CountryCitySelection from "./actcomp/CountryCitySelection";
import ActivitySelection from "./actcomp/ActivitySelection";
import ActivityDetailsPage from "./actcomp/ActivityDetailsPage";
import OptionSelection from "./actcomp/OptionSelection";
import VisitorSelection from "./actcomp/VisitorSelection";
import PriceSummary from "./actcomp/PriceSummary";
import BookingComplete from "./actcomp/BookingComplete";
import { fetchCountries, fetchCities, fetchActivities, fetchActivityOptions, fetchVisitorTypes, fetchTimePricingByOption, fetchTimeSlotsByOption } from "./actcomp/api";

const API_BASE_URL = (import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "http://localhost:3000") + "/api/activity";

const ActivityBooking = () => {
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [visitorDetails, setVisitorDetails] = useState({});
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("10:00");
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [lastBooking, setLastBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [activities, setActivities] = useState([]);
  const [activityOptions, setActivityOptions] = useState([]);
  const [visitorTypes, setVisitorTypes] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [slotsFromApi, setSlotsFromApi] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);

  useEffect(() => {
    fetchCountries(setCountries);
    fetchVisitorTypes(setVisitorTypes);
  }, []);

  useEffect(() => {
    const state = location?.state || {};
    const { countryId, cityId, activityId } = state;
    if (!countryId || !cityId) return;
    const run = async () => {
      try {
        const [countriesRes, citiesRes, actsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/user/countries`),
          axios.get(`${API_BASE_URL}/user/cities/${countryId}`),
          axios.get(`${API_BASE_URL}/user/activities/${cityId}`),
        ]);
        const countriesData = countriesRes.data?.data || [];
        const citiesData = citiesRes.data?.data || [];
        const actsData = actsRes.data?.data || [];
        const country = countriesData.find((c) => String(c._id) === String(countryId));
        const city = citiesData.find((c) => String(c._id) === String(cityId));
        if (!country || !city) return;
        setCountries(countriesData);
        setSelectedCountry(country);
        setSelectedCity(city);
        setCities(citiesData);
        setActivities(actsData);
        if (activityId) {
          const activity = actsData.find((a) => String(a._id) === String(activityId));
          if (activity) {
            const optsRes = await axios.get(`${API_BASE_URL}/user/activity-options/${activity._id}`);
            setSelectedActivity(activity);
            setActivityOptions(optsRes.data?.data || []);
            setCurrentStep(2);
          }
        }
      } catch (e) {
        /* ignore */
      }
    };
    run();
  }, [location?.state]);

  const handleCountrySelect = async (country) => {
    setSelectedCountry(country);
    setSelectedCity(null);
    setSelectedActivity(null);
    setSelectedOption(null);
    setCities([]);
    setActivities([]);
    setActivityOptions([]);
    await fetchCities(country._id, setCities);
    // Don't change step - stay on step 1 to show cities
  };

  const handleCitySelect = async (city) => {
    setSelectedCity(city);
    setSelectedActivity(null);
    setSelectedOption(null);
    setActivities([]);
    setActivityOptions([]);
    await fetchActivities(city._id, setActivities);
    // Stay on step 1 to show activities in horizontal scroller
  };

  const handleActivitySelect = async (activity) => {
    setSelectedActivity(activity);
    setSelectedOption(null);
    await fetchActivityOptions(activity._id, setActivityOptions);
    // Move to step 2 (Activity Details) after selecting activity
    setCurrentStep(2);
  };

  const handleOptionSelect = async (option) => {
    setSelectedOption(option);
    let initialVisitors = [];
    if (option.pricingTiers && option.pricingTiers.length > 0) {
      initialVisitors = option.pricingTiers.map(t => ({
        type: t.type,
        typeId: t._id || t.type,
        count: 0,
        multiplier: 1.0, // Multiplier handled by explicit price
        minAge: t.minAge,
        maxAge: t.maxAge,
      }));
    } else {
      const allowedIds = option.allowedVisitorTypeIds || [];
      const typesToUse = allowedIds.length > 0
        ? visitorTypes.filter((vt) => allowedIds.some((id) => (id && id.toString ? id.toString() : id) === (vt._id && vt._id.toString ? vt._id.toString() : vt._id)))
        : visitorTypes;
      initialVisitors = typesToUse.map((vt) => ({
        type: vt.name,
        typeId: vt._id,
        count: 0,
        multiplier: vt.priceMultiplier,
        minAge: vt.minAge,
        maxAge: vt.maxAge,
      }));
    }
    setVisitors(initialVisitors);
    const rules = await fetchTimePricingByOption(option._id);
    const apiSlots = await fetchTimeSlotsByOption(option._id != null ? option._id : "");
    setSlotsFromApi(Array.isArray(apiSlots) ? apiSlots : []);
    setSelectedSlot(null);
    const slots = new Set();
    rules.forEach((r) => {
      if (r.dayType === "allday") {
        ["09:00", "10:00", "11:00", "12:00", "14:00", "15:00", "16:00", "17:00"].forEach((t) => slots.add(t));
      } else {
        const start = r.startTime || "09:00";
        const end = r.endTime || "17:00";
        for (let h = parseInt(start.slice(0, 2), 10); h <= parseInt(end.slice(0, 2), 10); h++) {
          slots.add(`${String(h).padStart(2, "0")}:00`);
        }
      }
    });
    const arr = Array.from(slots).sort();
    const finalSlots = arr.length > 0 ? arr : ["09:00", "10:00", "11:00", "14:00", "15:00", "16:00"];
    setTimeSlots(finalSlots);
    setSelectedTime(finalSlots[0] || "10:00");
    setCurrentStep(4);
  };

  const handleVisitorChange = (typeId, count) => {
    const newCount = parseInt(count) || 0;
    setVisitors((prev) =>
      prev.map((v) =>
        v.typeId === typeId ? { ...v, count: newCount } : v
      )
    );
    
    setVisitorDetails((prev) => {
      const currentDetails = prev[typeId] || [];
      const newDetails = [];
      for (let i = 0; i < newCount; i++) {
        newDetails.push(currentDetails[i] || { name: "", phone: "", age: "" });
      }
      return { ...prev, [typeId]: newDetails };
    });
  };

  const incrementVisitor = (typeId) => {
    const currentCount = visitors.find((v) => v.typeId === typeId)?.count || 0;
    handleVisitorChange(typeId, currentCount + 1);
  };

  const decrementVisitor = (typeId) => {
    const currentCount = visitors.find((v) => v.typeId === typeId)?.count || 0;
    if (currentCount > 0) {
      handleVisitorChange(typeId, currentCount - 1);
    }
  };

  const handleVisitorDetailChange = (typeId, index, field, value) => {
    setVisitorDetails((prev) => {
      const typeDetails = prev[typeId] || [];
      const updatedDetails = [...typeDetails];
      updatedDetails[index] = { ...updatedDetails[index], [field]: value };
      return { ...prev, [typeId]: updatedDetails };
    });
  };

  const calculateTotalPrice = async () => {
    if (!selectedOption) {
      alert("Please select an activity option");
      return;
    }

    const totalVisitors = visitors.reduce((sum, v) => sum + v.count, 0);
    if (totalVisitors === 0) {
      alert("Please select at least one visitor");
      return;
    }

    if (!selectedDate) {
      alert("Please select a date");
      return;
    }

    if (slotsFromApi.length > 0 && !selectedSlot) {
      alert("Please select a time slot");
      return;
    }

    setLoading(true);
    const selectedDateTime = slotsFromApi.length > 0 && selectedSlot
      ? new Date(`${selectedDate}T${selectedSlot.startTime}:00`)
      : new Date(`${selectedDate}T${selectedTime}:00`);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/user/calculate-price`,
        {
          activityOptionId: selectedOption._id,
          visitors: visitors
            .filter((v) => v.count > 0)
            .map((v) => ({
              type: v.type,
              count: v.count,
            })),
          selectedDateTime: selectedDateTime.toISOString(),
        }
      );

      setPriceBreakdown(response.data.data);
      setCurrentStep(5);
    } catch (error) {
      console.error("Error calculating price:", error);
      alert("Error calculating price. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBookingCompleteWithPayment = async (paymentMethod) => {
    try {
      const adultMinAge = visitors.length ? Math.max(...visitors.map((v) => Number(v.minAge) || 0)) : 18;
      const allDetailsFilled = priceBreakdown?.breakdown?.every((item) => {
        const visitorType = visitors.find((v) => v.type === item.type);
        if (!visitorType || item.count === 0) return true;
        const typeDetails = visitorDetails[visitorType.typeId] || [];
        const isChildType = (Number(visitorType.maxAge) || 0) < adultMinAge;
        return (
          typeDetails.length === item.count &&
          typeDetails.every((detail) => {
            if (!detail.name || detail.age === undefined || detail.age === "") return false;
            if (!isChildType && !detail.phone) return false;
            return true;
          })
        );
      });

      if (!allDetailsFilled) {
        alert("Please fill in all visitor details (name and age for each; phone is required for adults only) before confirming.");
        return;
      }

      const visitorsWithDetails = visitors
        .filter((v) => v.count > 0)
        .map((v) => {
          const typeDetails = visitorDetails[v.typeId] || [];
          return {
            type: v.type,
            typeId: v.typeId,
            count: v.count,
            details: typeDetails.map((detail) => ({
              name: detail.name,
              phone: detail.phone,
              age: parseInt(detail.age) || 0,
            })),
          };
        });

      const bookingDateTime = slotsFromApi.length > 0 && selectedSlot
        ? new Date(`${selectedDate}T${selectedSlot.startTime}:00`)
        : new Date(`${selectedDate}T${selectedTime}:00`);
      const bookingData = {
        activityOptionId: selectedOption._id,
        visitors: visitorsWithDetails,
        selectedDateTime: bookingDateTime.toISOString(),
        totalPrice: priceBreakdown?.total || 0,
        currency: priceBreakdown?.currency || selectedCountry?.currency || "",
        customerName: "",
        customerEmail: "",
        customerPhone: "",
        paymentMethod: paymentMethod || "card",
        priceBreakdown: priceBreakdown || null,
      };
      if (selectedSlot?._id) {
        bookingData.slotId = selectedSlot._id;
        bookingData.selectedDate = selectedDate;
      }

      const response = await axios.post(`${API_BASE_URL}/user/bookings`, bookingData, {
        withCredentials: true,
      });

      if (response.data.success) {
        setLastBooking(response.data.data || null);
        setBookingComplete(true);
      } else {
        throw new Error(response.data.error || "Booking failed");
      }
    } catch (error) {
      console.error("Error completing booking:", error);
      const msg = error.response?.data?.error || error.message || "Error completing booking. Please try again.";
      alert(msg);
    }
  };

  const resetBookingFlow = () => {
    setCurrentStep(1);
    setSelectedCountry(null);
    setSelectedCity(null);
    setSelectedActivity(null);
    setSelectedOption(null);
    setSelectedDate("");
    setSelectedTime("10:00");
    setVisitors([]);
    setVisitorDetails({});
    setPriceBreakdown(null);
    setBookingComplete(false);
    setLastBooking(null);
    setSearchTerm("");
  };

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goNext = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const steps = [
    { num: 1, label: "Destination", short: "Where" },
    { num: 2, label: "Details", short: "Details" },
    { num: 3, label: "Package", short: "Package" },
    { num: 4, label: "Guests & Time", short: "When" },
    { num: 5, label: "Review & Pay", short: "Pay" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-blue-50/20 to-white">
      {/* Hero / Page title */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-white/20 rounded-xl">
              <FaTicketAlt className="text-2xl" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Book an Activity</h1>
              <p className="text-blue-100 text-sm sm:text-base mt-0.5">Choose destination → activity details → package → guests → pay.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {bookingComplete ? (
          <BookingComplete
            lastBooking={lastBooking}
            selectedActivity={selectedActivity}
            selectedOption={selectedOption}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            visitors={visitors}
            priceBreakdown={priceBreakdown}
            selectedCountry={selectedCountry}
            resetBookingFlow={resetBookingFlow}
          />
        ) : (
          <>
            {/* Stepper – clear progress */}
            <div className="mb-6 sm:mb-8">
              <div className="flex items-center justify-between max-w-2xl mx-auto">
                {steps.map((step, i) => {
                  const isActive = currentStep === step.num;
                  const isDone = currentStep > step.num;
                  return (
                    <div key={step.num} className="flex flex-1 items-center">
                      <div className="flex flex-col items-center flex-1">
                        <div
                          className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                            isDone ? "bg-green-500 text-white" : isActive ? "bg-blue-600 text-white ring-4 ring-blue-200" : "bg-slate-200 text-slate-500"
                          }`}
                        >
                          {isDone ? "✓" : step.num}
                        </div>
                        <span className={`mt-2 text-xs font-medium hidden sm:block ${isActive ? "text-blue-600" : isDone ? "text-green-600" : "text-slate-500"}`}>
                          {step.label}
                        </span>
                      </div>
                      {i < steps.length - 1 && (
                        <div className={`flex-1 h-1 mx-1 rounded-full max-w-[60px] ${isDone ? "bg-green-400" : "bg-slate-200"}`} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
              {/* Step header – compact */}
              <div className="border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-4 bg-slate-50/80">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                      {currentStep === 1 && (selectedCity ? `Activities in ${selectedCity.name}` : selectedCountry ? `Cities in ${selectedCountry.name}` : "Choose Your Destination")}
                      {currentStep === 2 && "Activity Details"}
                      {currentStep === 3 && "Choose Package"}
                      {currentStep === 4 && "Add Visitors & Time"}
                      {currentStep === 5 && "Review & Book"}
                    </h2>
                    <p className="text-slate-600 text-sm mt-0.5">
                      {currentStep === 1 && (selectedActivity ? `Selected: ${selectedActivity.name}` : selectedCity ? `Discover activities in ${selectedCity.name}` : selectedCountry ? `Pick a city in ${selectedCountry.name}` : "Select a country to start")}
                      {currentStep === 2 && "View details, images and packages"}
                      {currentStep === 3 && "Select the right package for your group"}
                      {currentStep === 4 && "Add guests and pick date & time"}
                      {currentStep === 5 && "Review details and complete payment"}
                    </p>
                  </div>
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={goBack}
                      className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                      Back
                    </button>
                  )}
                </div>
              </div>

              {/* Step content */}
              <div className="p-4 sm:p-6 lg:p-8">
                {currentStep === 1 && (
                  <CountryCitySelection
                    countries={countries}
                    cities={cities}
                    activities={activities}
                    selectedCountry={selectedCountry}
                    selectedCity={selectedCity}
                    selectedActivity={selectedActivity}
                    handleCountrySelect={handleCountrySelect}
                    handleCitySelect={handleCitySelect}
                    handleActivitySelect={handleActivitySelect}
                    API_BASE_URL={API_BASE_URL}
                  />
                )}

                {currentStep === 2 && (
                  <ActivityDetailsPage
                    selectedActivity={selectedActivity}
                    activityOptions={activityOptions}
                    selectedCountry={selectedCountry}
                    selectedCity={selectedCity}
                    API_BASE_URL={API_BASE_URL}
                    onContinue={() => setCurrentStep(3)}
                  />
                )}
                
                {currentStep === 3 && (
                  <OptionSelection
                    activityOptions={activityOptions}
                    selectedOption={selectedOption}
                    handleOptionSelect={handleOptionSelect}
                    selectedCountry={selectedCountry}
                  />
                )}
                
                {currentStep === 4 && (
                  <>
                    {!selectedOption ? (
                      <div className="text-center py-14 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-300">
                        <FaTag className="w-14 h-14 text-slate-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No package selected</h3>
                        <p className="text-slate-600 text-sm mb-5">Go back and choose a package to continue.</p>
                        <button
                          type="button"
                          onClick={() => setCurrentStep(3)}
                          className="px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
                        >
                          Back to packages
                        </button>
                      </div>
                    ) : (
                      <VisitorSelection
                        visitors={visitors}
                        incrementVisitor={incrementVisitor}
                        decrementVisitor={decrementVisitor}
                        selectedDate={selectedDate}
                        setSelectedDate={setSelectedDate}
                        selectedTime={selectedTime}
                        setSelectedTime={setSelectedTime}
                        selectedOption={selectedOption}
                        calculateTotalPrice={calculateTotalPrice}
                        loading={loading}
                        timeSlots={timeSlots}
                        slotsFromApi={slotsFromApi}
                        selectedSlot={selectedSlot}
                        setSelectedSlot={setSelectedSlot}
                      />
                    )}
                  </>
                )}
                
                {currentStep === 5 && (
                  <PriceSummary
                    selectedCountry={selectedCountry}
                    selectedCity={selectedCity}
                    selectedActivity={selectedActivity}
                    selectedOption={selectedOption}
                    selectedDate={selectedDate}
                    selectedTime={selectedTime}
                    selectedSlot={selectedSlot}
                    priceBreakdown={priceBreakdown}
                    visitors={visitors}
                    visitorDetails={visitorDetails}
                    handleVisitorDetailChange={handleVisitorDetailChange}
                    onBookingComplete={handleBookingCompleteWithPayment}
                  />
                )}
              </div>
            </div>
          </>
        )}

        {/* Footer – progress & actions */}
        {!bookingComplete && currentStep !== 5 && (
          <div className="mt-6 flex flex-wrap justify-between items-center gap-4 p-4 sm:p-5 bg-white rounded-xl border border-slate-200 shadow-sm">
            <button
              type="button"
              onClick={resetBookingFlow}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-4 py-2.5 rounded-xl transition-colors"
            >
              Start Over
            </button>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-500">
                Step {currentStep} of 5
              </span>
              {currentStep === 3 && selectedOption && (
                <button
                  type="button"
                  onClick={() => setCurrentStep(4)}
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl shadow-md hover:shadow-lg transition-all"
                >
                  Continue to Guests & Time →
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityBooking;